import React, { useState } from 'react';
import api from '../services/api';

/**
 * ApplyRCModal - Simplified modal for applying RC (Recibo de Caja) to an order
 * Used in RouteReceptionTab for quick payment validation
 */
const ApplyRCModal = ({ order, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        payment_method: order?.payment_method || 'efectivo',
        amount: order?.amount || '',
        bank_details: '',
        reference: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Call cartera validation endpoint to create RC
            const response = await api.post('/cartera/validate-payment', {
                order_id: order.order_id,
                action: 'approve',
                payment_method: formData.payment_method,
                amount: parseFloat(formData.amount),
                bank_details: formData.bank_details || null,
                reference: formData.reference || null
            });

            if (response.data.success) {
                if (onSuccess) {
                    onSuccess(response.data);
                }
            } else {
                setError(response.data.message || 'Error al aplicar RC');
            }
        } catch (err) {
            console.error('Error applying RC:', err);
            setError(err.response?.data?.error || err.response?.data?.message || 'Error al aplicar RC');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-xl font-bold">Aplicar RC</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            {order?.order_number} - {order?.customer_name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Payment Method */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Método de pago:
                        </label>
                        <select
                            value={formData.payment_method}
                            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="efectivo">Efectivo</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="mixto">Mixto</option>
                            <option value="credito">Crédito</option>
                        </select>
                    </div>

                    {/* Amount */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Monto a aplicar:
                        </label>
                        <input
                            type="number"
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Ej: 50000"
                            required
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            Sugerido: {formatCurrency(order?.amount)}
                        </div>
                    </div>

                    {/* Bank Details (for transfers) */}
                    {(formData.payment_method === 'transferencia' || formData.payment_method === 'mixto') && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Banco:
                            </label>
                            <input
                                type="text"
                                value={formData.bank_details}
                                onChange={(e) => setFormData({ ...formData, bank_details: e.target.value })}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="Ej: Nequi, Bancolombia"
                            />
                        </div>
                    )}

                    {/* Reference */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Referencia (opcional):
                        </label>
                        <input
                            type="text"
                            value={formData.reference}
                            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Número de referencia o nota"
                        />
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
                        >
                            {loading ? '⏳ Aplicando...' : '✅ Aplicar RC'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ApplyRCModal;
